// client/src/components/pages/Settings/AccountSetup.tsx (converted from AccountSetup.jsx,
// ST-00226 — CLAUDE.md TypeScript rule). Settings → Route Groupings.
//
// ST-00226:
//   - RSO-14: Create / Edit / Delete need the Edit Routes permission (routes.edit), as the
//     server now enforces; everyone else who can open the page sees the list read-only.
//   - RSO-1: every dispatch → receipt pair of a grouping needs a location the organisation
//     owns. A tenant user's save with such pairs is refused (the modal shows why); a global
//     admin's goes through with a warning, shown here after the save.
import { useState, useEffect, useMemo } from 'react';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaTimes,
  FaTruck,
  FaWarehouse,
  FaCheck,
  FaMinus,
  FaExclamationCircle,
} from 'react-icons/fa';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import GroupingModal from './GroupingModal';
import type {
  GroupingApiError,
  GroupingSaveWarning,
  RouteGrouping,
  SavedRouteGrouping,
} from '../../../types/clientPages/routeGroupings';

type LocationAccent = 'dispatch' | 'receipt';

interface LocationCellProps {
  locations?: string[] | null;
  accent: LocationAccent;
}

interface DeleteConfirmDialogProps {
  grouping: RouteGrouping;
  onCancel: () => void;
  onConfirm: () => void;
  deleting: boolean;
}

// Compact location list for a table cell: first few as chips, rest behind "+N"
const LocationCell = ({ locations, accent }: LocationCellProps) => {
  const MAX_VISIBLE = 3;
  const chipClass =
    accent === 'dispatch'
      ? 'bg-orange-50 border-orange-200 text-orange-900'
      : 'bg-emerald-50 border-emerald-200 text-emerald-900';

  if (!locations || locations.length === 0) {
    return <span className="text-sm text-slate-400">None</span>;
  }

  const visible = locations.slice(0, MAX_VISIBLE);
  const overflow = locations.slice(MAX_VISIBLE);

  return (
    <div className="flex flex-wrap gap-1 max-w-xs">
      {visible.map((loc) => (
        <span
          key={loc}
          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${chipClass}`}
        >
          {loc}
        </span>
      ))}
      {overflow.length > 0 && (
        <span
          title={overflow.join(', ')}
          className="inline-flex items-center px-2 py-0.5 text-xs font-medium border border-slate-200 bg-slate-50 text-slate-600 rounded-full cursor-default tabular-nums"
        >
          +{overflow.length} more
        </span>
      )}
    </div>
  );
};

// Styled destructive-action confirmation (replaces window.confirm)
const DeleteConfirmDialog = ({
  grouping,
  onCancel,
  onConfirm,
  deleting,
}: DeleteConfirmDialogProps) => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 bg-slate-900/50 dark:bg-black/50 flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-grouping-title"
        className="bg-surface rounded-xl shadow-2xl w-full max-w-md p-6 animate-fadeIn motion-reduce:animate-none"
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
            <FaTrash className="w-4 h-4 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 id="delete-grouping-title" className="text-base font-bold text-slate-900">
              Delete grouping?
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              <span className="font-medium text-slate-900">"{grouping.name}"</span> will be
              permanently removed. Summaries and reports that use it will no longer be available.
              This cannot be undone.
            </p>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={deleting}
            className="px-4 py-2 text-sm font-medium border border-slate-300 rounded-md text-slate-700 bg-surface hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className={`px-4 py-2 text-sm font-medium rounded-md text-white transition-colors ${
              deleting ? 'bg-slate-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {deleting ? 'Deleting...' : 'Delete Grouping'}
          </button>
        </div>
      </div>
    </div>
  );
};

const AccountSetup = () => {
  const { hasPermission } = useAuth();
  // RSO-14: the server refuses grouping changes without routes.edit (admin tiers hold '*').
  const canEdit = hasPermission('routes.edit');
  const [groupings, setGroupings] = useState<RouteGrouping[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<GroupingSaveWarning | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGrouping, setEditingGrouping] = useState<RouteGrouping | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RouteGrouping | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchGroupings();
  }, []);

  const fetchGroupings = async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch groupings without date range (get all groupings)
      const response = await api.routeSummary.getGroupings();
      setGroupings(Array.isArray(response.data) ? (response.data as RouteGrouping[]) : []);
    } catch (err) {
      console.error('Error fetching groupings:', err);
      setError('Failed to load groupings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredGroupings = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groupings;
    return groupings.filter((g) => {
      const haystack = [
        g.name,
        g.description || '',
        ...(g.dispatchLocations || []),
        ...(g.receiptLocations || []),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [groupings, search]);

  const handleCreateGrouping = () => {
    setEditingGrouping(null);
    setIsModalOpen(true);
  };

  const handleEditGrouping = (grouping: RouteGrouping) => {
    setEditingGrouping(grouping);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.routeSummary.deleteGrouping(deleteTarget.id);
      setDeleteTarget(null);
      await fetchGroupings();
    } catch (err) {
      console.error('Error deleting grouping:', err);
      setDeleteTarget(null);
      const e = err as GroupingApiError;
      setError(
        e.response?.status === 403
          ? e.response.data?.message || 'You do not have permission to delete route groupings.'
          : 'Failed to delete grouping. Please try again.'
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingGrouping(null);
  };

  const handleModalSave = async (saved?: SavedRouteGrouping) => {
    // A global admin's save that the organisation cannot fully see (ST-00226).
    setWarning(saved?.warning ?? null);
    await fetchGroupings();
    handleModalClose();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Section */}
      <div className="sticky top-0 z-10 bg-surface border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Account Setup</h1>
            <p className="text-slate-600 text-sm mt-1">
              Manage route groupings and organizational data
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Route Groupings Section */}
        <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-slate-900">Route Groupings</h2>
                  {!loading && groupings.length > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 tabular-nums">
                      {groupings.length}
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  Group dispatch and receipt locations into routes for analysis and reporting
                </p>
              </div>
              {canEdit && (
                <button
                  onClick={handleCreateGrouping}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors shadow-sm font-medium text-sm flex-shrink-0"
                >
                  <FaPlus className="w-3 h-3" />
                  Create Grouping
                </button>
              )}
            </div>

            {/* Search — only worth showing once the list has some size */}
            {!loading && groupings.length > 5 && (
              <div className="relative mt-4 max-w-sm">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or location..."
                  className="w-full pl-9 pr-8 py-2 text-sm border border-slate-300 rounded-md bg-surface text-slate-900 placeholder-slate-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25 transition-colors"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700 transition-colors"
                  >
                    <FaTimes className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="p-6">
            {/* Read-only for people without Edit Routes (RSO-14) */}
            {!canEdit && (
              <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 mb-4">
                You can view the route groupings. Creating, changing or deleting them needs the Edit
                Routes permission — ask an administrator.
              </p>
            )}

            {/* Warning after a global admin's save (ST-00226) */}
            {warning && (
              <div
                role="status"
                className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4 flex items-start gap-3"
              >
                <FaExclamationCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-amber-900">{warning.message}</p>
                  {warning.pairs.length > 0 && (
                    <ul className="mt-1 text-xs text-amber-800 list-disc pl-4 space-y-0.5">
                      {warning.pairs.slice(0, 20).map((p) => (
                        <li key={`${p.dispatchLocation}→${p.receiptLocation}`}>
                          {p.dispatchLocation} → {p.receiptLocation}
                        </li>
                      ))}
                      {warning.pairs.length > 20 && <li>and {warning.pairs.length - 20} more</li>}
                    </ul>
                  )}
                </div>
                <button
                  onClick={() => setWarning(null)}
                  aria-label="Dismiss warning"
                  className="flex-shrink-0 text-amber-500 hover:text-amber-700 transition-colors"
                >
                  <FaTimes className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 flex items-start gap-3">
                <FaExclamationCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="flex-1 text-sm font-medium text-red-700">{error}</p>
                <button
                  onClick={() => setError(null)}
                  aria-label="Dismiss error"
                  className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors"
                >
                  <FaTimes className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="animate-pulse space-y-3" aria-label="Loading groupings">
                <div className="h-10 bg-slate-100 rounded-lg"></div>
                <div className="h-14 bg-slate-50 rounded-lg"></div>
                <div className="h-14 bg-slate-50 rounded-lg"></div>
                <div className="h-14 bg-slate-50 rounded-lg"></div>
              </div>
            ) : groupings.length === 0 ? (
              /* Empty State */
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                    <FaTruck className="w-5 h-5 text-orange-600" />
                  </div>
                  <div className="text-slate-300 text-xl font-light">→</div>
                  <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <FaWarehouse className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">No route groupings yet</h3>
                <p className="text-slate-600 mb-6 max-w-md mx-auto text-sm">
                  A grouping pairs dispatch locations with receipt locations to define a route. Once
                  created, it powers the Route Summary, Dashboard and reports.
                </p>
                {canEdit && (
                  <button
                    onClick={handleCreateGrouping}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors shadow-sm font-medium text-sm"
                  >
                    <FaPlus className="w-3 h-3" />
                    Create Your First Grouping
                  </button>
                )}
              </div>
            ) : filteredGroupings.length === 0 ? (
              /* No search matches */
              <div className="text-center py-10">
                <p className="text-sm text-slate-600">
                  No groupings match <span className="font-medium text-slate-900">"{search}"</span>
                </p>
                <button
                  onClick={() => setSearch('')}
                  className="mt-2 text-sm font-medium text-primary-700 hover:text-primary-800 transition-colors"
                >
                  Clear search
                </button>
              </div>
            ) : (
              /* Groupings Table */
              <div className="overflow-x-auto -mx-6 px-6">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Grouping
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        <span className="flex items-center gap-1.5">
                          <FaTruck className="w-3 h-3 text-orange-500" />
                          Dispatch
                        </span>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        <span className="flex items-center gap-1.5">
                          <FaWarehouse className="w-3 h-3 text-emerald-500" />
                          Receipt
                        </span>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Reporting
                      </th>
                      {canEdit && (
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-surface divide-y divide-slate-100">
                    {filteredGroupings.map((grouping) => (
                      <tr key={grouping.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 align-top">
                          <div className="text-sm font-medium text-slate-900">{grouping.name}</div>
                          {grouping.description && (
                            <div
                              className="text-xs text-slate-500 mt-0.5 max-w-[16rem] truncate"
                              title={grouping.description}
                            >
                              {grouping.description}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <LocationCell locations={grouping.dispatchLocations} accent="dispatch" />
                        </td>
                        <td className="px-4 py-3 align-top">
                          <LocationCell locations={grouping.receiptLocations} accent="receipt" />
                        </td>
                        <td className="px-4 py-3 align-top whitespace-nowrap">
                          {grouping.includeInReporting !== false ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <FaCheck className="w-2.5 h-2.5" />
                              Included
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                              <FaMinus className="w-2.5 h-2.5" />
                              Excluded
                            </span>
                          )}
                        </td>
                        {canEdit && (
                          <td className="px-4 py-3 align-top whitespace-nowrap text-right">
                            <div className="inline-flex items-center gap-1">
                              <button
                                onClick={() => handleEditGrouping(grouping)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-slate-300 text-slate-700 rounded-md bg-surface hover:bg-slate-50 transition-colors"
                                title={`Edit ${grouping.name}`}
                              >
                                <FaEdit className="w-3 h-3" />
                                Edit
                              </button>
                              <button
                                onClick={() => setDeleteTarget(grouping)}
                                aria-label={`Delete ${grouping.name}`}
                                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                title={`Delete ${grouping.name}`}
                              >
                                <FaTrash className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Grouping Modal */}
      {isModalOpen && canEdit && (
        <GroupingModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSave={handleModalSave}
          grouping={editingGrouping}
        />
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <DeleteConfirmDialog
          grouping={deleteTarget}
          deleting={deleting}
          onCancel={() => !deleting && setDeleteTarget(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
};

export default AccountSetup;
